<script>
  import { createEventDispatcher } from "svelte";
  import Select from "svelte-select/Select.svelte";
  import NotificationModal from "../../Components/Common/NotificationModal.svelte";
  import { apiMethods } from "../../Services/index.svelte";
  import { user } from "../../stores";
  import { searchGroupsFromCache } from "../../Utils/DataLoaders.svelte";
  import { CalendarEventCategoryTypes } from "../../Utils/utilEnums.svelte";
  import { showError, showInfo } from "../../Utils/utilFunctions.svelte";

  export let isModalOpen = false,
    allCats = [],
    allowGroupEdit,
    selectedGroup,
    catObj = {};

  const dispatch = createEventDispatcher();

  let isUpdating = false,
    previousKeyDown;

  $: isSubmitButtonDisabled =
    !catObj.name ||
    !catObj.calendarColour ||
    isNameConflictWithSystemCategories ||
    isNameDup;

  $: isNameConflictWithSystemCategories = checkCustomCategoryName(
    catObj,
    allCats
  );

  $: isNameDup = checkNameDup(catObj, allCats);

  const init = () => {
    previousKeyDown = document.onkeydown;
    document.onkeydown = (event) => {
      if (event.key === "Escape") closeModal();
    };
  };

  const checkCustomCategoryName = (thisCat, all) => {
    if (isSystemCategories) return false;
    const allSystemNames = [];
    if (all.length) {
      all.map((cat) => {
        if (cat.categoryType !== CalendarEventCategoryTypes.Custom && cat.name)
          allSystemNames.push(cat.name);
      });
    }
    return allSystemNames.some(
      (systemCatName) =>
        systemCatName.toLowerCase() === thisCat?.name?.trim().toLowerCase()
    );
  };

  const checkNameDup = (thisCat, all) => {
    if (isSystemCategories) return false;
    const allCustomNames = [];
    if (all.length) {
      all.map((cat) => {
        if (
          cat.categoryType === CalendarEventCategoryTypes.Custom &&
          cat.name &&
          cat.id !== catObj.id
        )
          allCustomNames.push(cat.name);
      });
    }
    return allCustomNames.some(
      (customName) =>
        customName.toLowerCase() === thisCat?.name?.trim().toLowerCase()
    );
  };

  $: isSystemCategories =
    catObj?.categoryType !== CalendarEventCategoryTypes.Custom;

  const closeModal = () => {
    isModalOpen = false;
    catObj = {};
    selectedGroup = undefined;
    if (previousKeyDown) {
      document.onkeydown = previousKeyDown;
      previousKeyDown = undefined;
    }
  };

  const proceedCategory = () => {
    if (catObj.id) {
      editCategory();
    } else {
      createCategory();
    }
  };

  const createCategory = () => {
    isUpdating = true;
    if (allowGroupEdit) {
      catObj.groupId = selectedGroup?.id ?? 0;
    } else {
      catObj.groupId = $user.groupId ?? 0;
    }
    apiMethods.postMethod
      .createCalendarEventCategory(catObj)
      .then((res) => {
        isUpdating = false;
        if (res.error) throw error;
        else {
          showInfo("New Calendar Event Category Created");
          dispatch("reloadList");
          closeModal();
        }
      })
      .catch((err) => {
        showError(err);
        isUpdating = false;
      });
  };

  const editCategory = () => {
    isUpdating = true;
    apiMethods.putMethod
      .updateCalendarEventCategory(catObj)
      .then((res) => {
        isUpdating = false;
        if (res.error) throw error;
        else {
          showInfo("Calendar Event Category Updated");
          dispatch("reloadList");
          closeModal();
        }
      })
      .catch((err) => {
        showError(err);
        isUpdating = false;
      });
  };
</script>

<div
  class={isModalOpen ? "modal modal-open sm:modal-middle" : "modal"}
  use:init
>
  <div
    class="modal-box no-transform h-auto max-h-[600px] p-0 flex flex-col overflow-y-visible"
  >
    <div
      class="flex flex-row w-full px-6 py-3 justify-between text-[#fff] bg-[#8a3ed7] rounded-tl-xl rounded-tr-xl "
    >
      <h3 class="font-medium text-lg flex-0">
        {catObj.id ? "Edit" : "Create"}
        Calendar Event Category
      </h3>
      <button class="btn btn-circle btn-sm btn-ghost" on:click={closeModal}>
        <i class="fa fa-times" aria-hidden="true" />
      </button>
    </div>

    <div class="flex flex-1 flex-col gap-y-4 overflow-y-visible p-6">
      <div class="flex gap-x-4 items-center">
        <p class="w-40">Name:</p>
        <input
          style={isNameDup || isNameConflictWithSystemCategories
            ? "border: 2px solid red;"
            : ""}
          type="text"
          class="input input-sm input-bordered flex-1"
          autocomplete="off"
          disabled={isSystemCategories}
          bind:value={catObj.name}
        />
      </div>

      {#if isNameConflictWithSystemCategories}
        <div class="flex">
          <p class="text-error">
            Cannot re-create system calendar event categories
          </p>
        </div>
      {/if}

      {#if isNameDup}
        <div class="flex">
          <p class="text-error">Duplicated category name</p>
        </div>
      {/if}

      {#if allowGroupEdit && !catObj.id}
        <div class="flex gap-x-4 items-center">
          <p class="w-40">Group:</p>
          <Select
            bind:value={selectedGroup}
            class="flex flex-1 max-h-[35px] rounded-full"
            loadOptions={searchGroupsFromCache}
            itemId="id"
            label="name"
            --list-z-index="3"
            noOptionsMessage="No Groups Found"
          />
        </div>
      {/if}

      <div class="flex gap-x-4 items-center">
        <p class="w-40">Colour On Calendar:</p>
        <input
          type="color"
          class="input input-sm input-bordered flex-1"
          autocomplete="off"
          bind:value={catObj.calendarColour}
        />
      </div>

      <div class="modal-action flex items-center justify-end gap-x-4">
        <button
          class="btn btn-error text-white btn-sm border-none w-36"
          disabled={isUpdating}
          on:click={closeModal}>Close</button
        >

        <button
          class={`btn btn-secondary text-white btn-sm border-none ${
            isUpdating ? "loading w-40" : "w-36"
          }`}
          disabled={isSubmitButtonDisabled}
          on:click={proceedCategory}
        >
          {isUpdating
            ? catObj.id
              ? "Updating"
              : "Creating"
            : catObj.id
            ? "Update"
            : "Create"}
        </button>
      </div>
    </div>
  </div>
</div>
